import './SalesforceRelationships.scss';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdAdd, MdError } from 'react-icons/md';
import { RiPencilFill } from 'react-icons/ri';
import {
  accountKey,
  BillingAccount,
  SalesforceRelationship,
  UpdateSalesforceRelationshipsBody
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import {
  CustomModalEditWidgetHeader,
  ModalEditWidgetHandle,
  SaveChangesInput,
  TableRow
} from '@Components/ModalEditWidget/modal-edit-constants';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { IconButton } from '@src/Components/IconButton/IconButton';
import { useSetError } from '../../redux/modals';
import { BillingAutomationLambdaFetch, fetchCometSelfInvoke } from '../../utils/fetch-utils';
import { accountLabel, textCell, ThreeLineOption } from './billing-utils';
import { EditSalesforceRelationshipModal } from './EditSalesforceRelationshipModal';

const ROW_BASE_HEIGHT = 60;
const ROW_LINE_HEIGHT = 32;
const ACCOUNTS_PER_LINE = 4;

const estimateRowHeight = (row?: SalesforceRelationship): number => {
  const accountCount = row?.accounts?.length ?? 0;
  const lines = Math.max(1, Math.ceil(accountCount / ACCOUNTS_PER_LINE));
  return ROW_BASE_HEIGHT + (lines - 1) * ROW_LINE_HEIGHT;
};

type MappingError = 'failed' | 'duplicate' | null;

interface MappingState {
  pendingAccounts: Partial<Record<string, BillingAccount[]>>;
  unmappedAccounts: BillingAccount[];
}

const SFRelationshipOptionLabel = ({
  label,
  value
}: {
  label: string;
  value: string;
}): ReactNode => <ThreeLineOption label={label} subLabel={`Relationship ID: ${value}`} />;

interface SalesforceRelationshipsProps {
  company: string;
  userEmail: string;
}

export const SalesforceRelationships = ({ company, userEmail }: SalesforceRelationshipsProps) => {
  const setError = useSetError();
  const [isFetching, setIsFetching] = useState(true);
  const [fetchedRelationships, setFetchedRelationships] = useState<SalesforceRelationship[]>([]);
  const [mappingState, setMappingState] = useState<MappingState>({
    pendingAccounts: {},
    unmappedAccounts: []
  });
  const [editingRelationship, setEditingRelationship] = useState<
    SalesforceRelationship | undefined
  >();
  const [editingRowKey, setEditingRowKey] = useState<string | undefined>();
  const [showEditModal, setShowEditModal] = useState(false);
  const [mappingError, setMappingError] = useState<MappingError>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showToggleWarning, setShowToggleWarning] = useState(false);

  const widgetRef = useRef<ModalEditWidgetHandle<SalesforceRelationship>>(null);
  const pendingSaveRef = useRef<{
    modifiedData: SalesforceRelationship[];
    cleanup: () => void;
  } | null>(null);

  const loadData = useCallback(async () => {
    // `company` defaults to '' in Redux before it hydrates; wait for a real value.
    if (!company) {
      return;
    }
    setIsFetching(true);
    try {
      const { relationships, unmappedAccounts: accounts } = await fetchCometSelfInvoke<
        { company: string },
        { relationships: SalesforceRelationship[]; unmappedAccounts: BillingAccount[] }
      >(
        BillingAutomationLambdaFetch,
        '/fetchSalesforceRelationships',
        { company },
        { method: 'GET', noTimeout: true }
      );
      setFetchedRelationships(relationships);
      setMappingState({ pendingAccounts: {}, unmappedAccounts: accounts });
    } catch (e) {
      setError({ message: e.message, title: 'Failed to load Salesforce data', reportError: e });
    } finally {
      setIsFetching(false);
    }
  }, [setError, company]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const runSave = useCallback(
    async (modifiedData: SalesforceRelationship[], cleanup: () => void) => {
      try {
        const body: UpdateSalesforceRelationshipsBody = {
          company,
          relationships: modifiedData.map(r => ({ salesforceId: r.id, accounts: r.accounts })),
          email: userEmail
        };
        await BillingAutomationLambdaFetch('/updateSalesforceRelationships', {
          method: 'POST',
          body
        });
        cleanup();
        await loadData();
      } catch (e) {
        if (e.status === 409 || e.code === 'DUPLICATE') {
          setMappingError('duplicate');
        } else {
          setMappingError('failed');
        }
      }
    },
    [loadData, company, userEmail]
  );

  const handleSave = useCallback(
    ({ modifiedData, cleanup }: SaveChangesInput<SalesforceRelationship>) => {
      pendingSaveRef.current = { modifiedData, cleanup };
      runSave(modifiedData, cleanup);
    },
    [runSave]
  );

  const handleRetry = useCallback(() => {
    if (pendingSaveRef.current) {
      runSave(pendingSaveRef.current.modifiedData, pendingSaveRef.current.cleanup);
    }
  }, [runSave]);

  const visibleRelationships = useMemo(
    () => (includeInactive ? fetchedRelationships : fetchedRelationships.filter(r => r.active)),
    [fetchedRelationships, includeInactive]
  );

  const handleStageMapping = useCallback(
    (account: BillingAccount, targetRelId: string) => {
      const targetRel = visibleRelationships.find(r => r.id === targetRelId);
      if (!targetRel) return;
      const rowKey = String(visibleRelationships.findIndex(r => r.id === targetRelId));
      const currentAccounts = mappingState.pendingAccounts[targetRelId] ?? targetRel.accounts;
      const updatedAccounts = [...currentAccounts, account];
      widgetRef.current?.trackModify(rowKey, { ...targetRel, accounts: updatedAccounts });
      setMappingState(prev => ({
        pendingAccounts: { ...prev.pendingAccounts, [targetRelId]: updatedAccounts },
        unmappedAccounts: prev.unmappedAccounts.filter(a => accountKey(a) !== accountKey(account))
      }));
    },
    [visibleRelationships, mappingState.pendingAccounts]
  );

  const handleStageRelationshipEdit = useCallback(
    (updated: SalesforceRelationship) => {
      if (!editingRowKey || !editingRelationship) return;
      widgetRef.current?.trackModify(editingRowKey, updated);
      const previousAccounts =
        mappingState.pendingAccounts[editingRelationship.id] ??
        visibleRelationships.find(r => r.id === editingRelationship.id)?.accounts ??
        [];
      const updatedKeys = new Set(updated.accounts.map(accountKey));
      setMappingState(prev => ({
        pendingAccounts: { ...prev.pendingAccounts, [editingRelationship.id]: updated.accounts },
        unmappedAccounts: [
          ...prev.unmappedAccounts.filter(a => !updatedKeys.has(accountKey(a))),
          ...previousAccounts.filter(a => !updatedKeys.has(accountKey(a)))
        ]
      }));
    },
    [editingRowKey, editingRelationship, visibleRelationships, mappingState.pendingAccounts]
  );

  const mergedRelationships = useMemo(
    () =>
      visibleRelationships.map(r => ({
        ...r,
        accounts: mappingState.pendingAccounts[r.id] ?? r.accounts
      })),
    [visibleRelationships, mappingState.pendingAccounts]
  );

  const relationshipOptions = useMemo(
    () => visibleRelationships.map(r => ({ value: r.id, label: r.name })),
    [visibleRelationships]
  );

  const tableHeaders: CustomModalEditWidgetHeader[] = useMemo(
    () => [
      {
        label: 'Salesforce Relationship',
        field: 'name',
        flex: 2,
        renderer: (row: SalesforceRelationship) => (
          <div className='sf-relationship-cell'>
            <span className='name'>{row.name}</span>
            <span className='id'>Relationship ID: {row.id}</span>
          </div>
        )
      },
      {
        label: 'Mapped Accounts',
        field: 'accounts',
        flex: 3,
        optionsGetter: (row: SalesforceRelationship) => row.accounts.map(accountLabel),
        renderer: (row: SalesforceRelationship) => {
          if (row.accounts.length === 0) {
            return (
              <div className='billing-cell-centered'>
                <Button
                  leadingElement={<MdAdd />}
                  onClick={() => {
                    setEditingRelationship(row);
                    setEditingRowKey(String(visibleRelationships.findIndex(r => r.id === row.id)));
                    setShowEditModal(true);
                  }}
                >
                  Map Accounts
                </Button>
              </div>
            );
          }
          return (
            <div className='account-pills billing-cell-centered'>
              {row.accounts.map(a => (
                <span key={accountKey(a)} className='account-pill'>
                  {accountLabel(a)}
                </span>
              ))}
            </div>
          );
        }
      },
      {
        label: 'Effective Date',
        field: 'effectiveDate',
        flex: 1,
        renderer: (row: SalesforceRelationship) => (
          <div className='billing-cell-centered'>{textCell(row.effectiveDate)}</div>
        )
      },
      {
        label: 'End Date',
        field: 'endDate',
        flex: 1,
        renderer: (row: SalesforceRelationship) => (
          <div className='billing-cell-centered'>{textCell(row.endDate)}</div>
        )
      },
      {
        label: '',
        field: 'id',
        flex: 0,
        hideInPendingChanges: true,
        renderer: (row: TableRow<SalesforceRelationship>) => (
          <IconButton
            className='edit-button billing-cell-centered'
            design={Design.BRAND}
            appearance={Appearance.EMPTY}
            size={Size.MD}
            onClick={() => {
              setEditingRelationship(row);
              setEditingRowKey(row.rowKey);
              setShowEditModal(true);
            }}
          >
            <RiPencilFill />
          </IconButton>
        )
      }
    ],
    [visibleRelationships]
  );

  const relationshipsTable = () => (
    <ModalEditWidget<SalesforceRelationship>
      ref={widgetRef}
      // Remount on toggle: rowKeys are positional indices into visibleRelationships, and the
      // widget's pending edits persist across re-renders. Remounting rebuilds indices and pending
      // state together so a toggle can't misattribute an unsaved staged change to the wrong row.
      key={includeInactive ? 'all' : 'active-only'}
      name='Salesforce Relationship Mapping'
      className='relationships-table'
      headerRightActions={
        <ToggleSwitch
          className='sf-active-toggle'
          label='Include inactive Salesforce Relationships'
          checked={includeInactive}
          onChange={() => {
            // Warn before discarding unsaved staged mappings (the view switch remounts the widget).
            if (Object.keys(mappingState.pendingAccounts).length > 0) {
              setShowToggleWarning(true);
            } else {
              setIncludeInactive(prev => !prev);
            }
          }}
          size={Size.LG}
        />
      }
      headers={tableHeaders}
      initialTableData={visibleRelationships}
      isLoading={isFetching}
      selectorOptions={{}}
      enableAdd={false}
      onSaveModal={handleSave}
      customEditor={{
        onRowClick: row => {
          setEditingRelationship(row);
          setEditingRowKey(row.rowKey);
          setShowEditModal(true);
        },
        onAddClick: () => {}
      }}
      rowHeightGetter={estimateRowHeight}
    />
  );

  return (
    <>
      <div className='salesforce-relationships'>
        {mappingState.unmappedAccounts.length > 0 && (
          <UnmappedAccountsBanner
            accounts={mappingState.unmappedAccounts}
            relationshipOptions={relationshipOptions}
            onMap={handleStageMapping}
          />
        )}
        <div className='table-container'>
          <div className='table-container-body'>{relationshipsTable()}</div>
        </div>
      </div>
      {editingRelationship && (
        <EditSalesforceRelationshipModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingRelationship(undefined);
            setEditingRowKey(undefined);
          }}
          onSave={handleStageRelationshipEdit}
          relationship={editingRelationship}
          unmappedAccounts={mappingState.unmappedAccounts}
          allRelationships={mergedRelationships}
        />
      )}
      <AlertDialog
        show={showToggleWarning}
        onHide={() => setShowToggleWarning(false)}
        intent={Intent.WARNING}
        icon={<MdError size={40} />}
        title='Discard unsaved changes?'
        body='Switching the view will discard your unsaved mapping changes.'
        primaryAction={{
          onClick: () => {
            setShowToggleWarning(false);
            setIncludeInactive(prev => !prev);
            loadData();
          },
          label: 'Discard & Switch'
        }}
        secondaryAction={{ onClick: () => setShowToggleWarning(false) }}
      />
      <AlertDialog
        show={mappingError === 'failed'}
        onHide={() => setMappingError(null)}
        intent={Intent.ERROR}
        icon={<MdError size={40} />}
        title='Mapping Failed'
        body='We couldn&apos;t map "[ACCT RETURNED FROM BACKEND]" right now. This might be a temporary connection issue.'
        primaryAction={{ onClick: handleRetry, label: 'Try Again' }}
        secondaryAction={{ onClick: () => setMappingError(null), label: 'Dismiss' }}
      />
      <AlertDialog
        show={mappingError === 'duplicate'}
        onHide={() => setMappingError(null)}
        intent={Intent.WARNING}
        icon={<MdError size={40} />}
        title='Duplicate Mapping Detected'
        body='"[ACCT RETURNED FROM BACKEND]" is already linked to Marketplaces. To change this, you&apos;ll need to unmap it from the original source first.'
        primaryAction={{ label: 'Close' }}
      />
    </>
  );
};

interface UnmappedAccountsBannerProps {
  accounts: BillingAccount[];
  relationshipOptions: { value: string; label: string }[];
  onMap: (account: BillingAccount, relId: string) => void;
}

const UnmappedAccountsBanner = ({
  accounts,
  relationshipOptions,
  onMap
}: UnmappedAccountsBannerProps) => (
  <div className='unmapped-accounts-banner'>
    <div className='unmapped-header'>
      <MdError />
      <span>Action Required: Unmapped Accounts ({accounts.length})</span>
    </div>
    <div className='unmapped-cards'>
      {accounts.map(account => (
        <div key={accountKey(account)} className='unmapped-card'>
          <div className='unmapped-card-info'>
            <p className='name'>{accountLabel(account)}</p>
            <p className='id'>Account ID: {account.account_id}</p>
          </div>
          <Dropdown
            size={Size.LG}
            searchable
            menuPortalTarget={document.body}
            className='unmapped-card-select'
            options={relationshipOptions}
            value={null}
            placeholder='Map to Salesforce Relationship'
            onChange={selected => {
              if (selected) {
                onMap(account, selected);
              }
            }}
            formatOptionLabel={SFRelationshipOptionLabel}
          />
        </div>
      ))}
    </div>
  </div>
);
